import { useAtomValue } from 'jotai';
import { ReactElement, useState } from 'react';
import { Button, Modal, InlineAlert, Input } from '@/components';
import {
    isNationalIdentityNumber,
    isFullName,
    isUnderCertainAge,
} from '@/shared/validators';
import { Utils } from '@/shared/Utils';
import { format, R } from '@/features/profile/resources/ProfileResources.resx';
import {
    ChildModalMode,
    ChildModalProps,
} from '@/features/shared/features/addOrEditChild/model';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
    isAdolescentEnabledAtom,
} from '@/caregiverSpace/shared/model';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { ModulusWarning } from '@/features/profile/shared/ui/ModulusWarning';
import { ParentalAuthorityWarning } from '@/features/profile/shared/ui/ParentalAuthorityWarning';
import { useChildForm } from '@/features/shared/features/addOrEditChild/lib';
import { ChildTerms } from './ChildTerms';

export function ChildModal(props: ChildModalProps) {
    return <ChildModalInternal {...props} />;
}

function ChildModalInternal(props: ChildModalProps) {
    const {
        register,
        handleSubmit,
        errors,
        onSubmit,
        onSubmitWithoutParentalCheck,
        status,
        isProcessing,
        reset,
        errorMessage,
        showModulusWarning,
        hideModulusWarning,
        showParentalAuthorityWarning,
        hideParentalAuthorityWarning,
        getValues,
        shouldShowCprError,
    } = useChildForm(props);

    const { mode, onClose, prefilledChild, canAlwaysEditName } = props;

    const isPrefilled = !!prefilledChild;
    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);
    const adultMinimumAge = useAtomValue(adultMinimumAgeAtom);
    const isAdolescentEnabled = useAtomValue(isAdolescentEnabledAtom);

    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

    return (
        <Modal
            onClose={() => {
                reset();
                onClose();
            }}
            title={generateTitle(mode)}
        >
            {showModulusWarning && (
                <ModulusWarning
                    onBack={hideModulusWarning}
                    onConfirm={onSubmit}
                    cprNumber={getValues().CPR}
                    isPending={isProcessing}
                />
            )}

            {showParentalAuthorityWarning && (
                <ParentalAuthorityWarning
                    onBack={hideParentalAuthorityWarning}
                    onConfirm={onSubmitWithoutParentalCheck}
                    isPending={isProcessing}
                />
            )}

            {!showModulusWarning &&
                !showParentalAuthorityWarning &&
                status !== 'error' && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <FormControl
                            label={R.Name}
                            error={errors.Name?.message}
                            className="mb-6"
                        >
                            <Input
                                label={R.FullName}
                                readOnly={
                                    canAlwaysEditName ? false : isPrefilled
                                }
                                {...register('Name', {
                                    required: R.PleaseEnterFullName,
                                    validate: {
                                        isFullName: (value: string) =>
                                            isFullName(value) ||
                                            R.PleaseEnterFullName,
                                    },
                                })}
                            />
                        </FormControl>

                        <FormControl
                            label={R.CprNumber}
                            error={
                                errors.CPR?.message ||
                                (mode.isEditing &&
                                    shouldShowCprError &&
                                    R.ParentalAuthorityCannotBeConfirmed)
                            }
                            className="mb-6"
                        >
                            <Input
                                label={R.CprNumber}
                                readOnly={isPrefilled}
                                className="mb-2"
                                placeholder={R.CprPlaceholder}
                                {...register('CPR', {
                                    required: R.CprRequired,
                                    setValueAs: (value) =>
                                        value.replace('-', ''),
                                    validate: {
                                        validCpr: (value: string) =>
                                            isNationalIdentityNumber(value) ||
                                            R.InvalidCpr,
                                        isUnderCertainAge: (value: string) =>
                                            isUnderCertainAge(
                                                value,
                                                isAdolescentEnabled
                                                    ? adolescentMinimumAge
                                                    : adultMinimumAge,
                                            ) ||
                                            format(
                                                R.CprTooOld,
                                                adolescentMinimumAge.toString(),
                                            ),
                                    },
                                })}
                                onInput={
                                    isPrefilled
                                        ? undefined
                                        : Utils.sanitizeNationalIdentifierInput
                                }
                            />
                        </FormControl>

                        {mode.isAdding && (
                            <ChildTerms
                                hasAcceptedTerms={hasAcceptedTerms}
                                setHasAcceptedTerms={setHasAcceptedTerms}
                            />
                        )}
                        <div className="mt-6 flex flex-col gap-4">
                            <footer className="text-right">
                                <ConfirmButton
                                    mode={props.mode}
                                    isProcessing={isProcessing}
                                    hasAcceptedTerms={hasAcceptedTerms}
                                />
                            </footer>
                        </div>
                    </form>
                )}

            {status === 'error' && !showParentalAuthorityWarning && (
                <InlineAlert onClose={reset} errorMessage={errorMessage} />
            )}
        </Modal>
    );
}

function ConfirmButton({
    mode,
    isProcessing,
    hasAcceptedTerms,
}: {
    mode: ChildModalMode;
    isProcessing: boolean;
    hasAcceptedTerms: boolean;
}): ReactElement {
    if (mode.isAdding) {
        return (
            <Button
                variant="primary"
                type="submit"
                className={isProcessing ? 'button--loading' : ''}
                disabled={!hasAcceptedTerms}
            >
                {R.ProfileConfirmAddKid}
            </Button>
        );
    }
    if (mode.isEditing) {
        return (
            <Button
                variant="primary"
                type="submit"
                className={isProcessing ? 'button--loading' : ''}
            >
                {R.ProfileConfirmEditKid}
            </Button>
        );
    }
}

const generateTitle = (mode: ChildModalMode): string => {
    if (mode.isEditing) {
        return R.Profile_EditChild;
    }
    if (mode.isAdding) {
        return R.Profile_AddChild;
    }
    return;
};
